import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, where, getDoc } from "firebase/firestore";
import { db } from "../firebase"; // Ana firebase dosyasından db'yi import ediyoruz

export interface CityFeatures {
  bus: boolean;
  metro: boolean;
  tram: boolean;
  ferry: boolean;
  train: boolean;
}

export interface CityLocation {
  latitude: number | undefined;
  longitude: number | undefined;
}

export interface FirebaseCity {
  id?: string;
  countryCode: string;
  countryName: string;
  cityCode: string;
  cityName: string;
  isActive: boolean;
  apiEndpoint: string;
  features: CityFeatures;
  transportFeatures?: any; // Detaylı ulaşım özellikleri (lines-stops-features için)
  routePlanning: boolean;
  announcements: boolean;
  description: string;
  dataSource: string;
  dataSourceUrl: string;
  createdAt: string;
  updatedAt: string;
  location?: CityLocation;
}

// Tüm şehirleri getir
export const getAllCities = async (): Promise<FirebaseCity[]> => {
  try {
    const citiesRef = collection(db, 'cities');
    const snapshot = await getDocs(citiesRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as FirebaseCity));
  } catch (error) {
    console.error('Şehirler getirilirken hata oluştu:', error);
    throw error;
  }
};

// Aktif şehirleri getir
export const getActiveCities = async (): Promise<FirebaseCity[]> => {
  try {
    const citiesRef = collection(db, 'cities');
    const q = query(citiesRef, where('isActive', '==', true));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as FirebaseCity));
  } catch (error) {
    console.error('Aktif şehirler getirilirken hata oluştu:', error);
    throw error;
  }
};

// Şehir ekle
export const addCity = async (city: Omit<FirebaseCity, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const now = new Date().toISOString();
    const cityData = {
      ...city,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(collection(db, 'cities'), cityData);
    return docRef.id;
  } catch (error) {
    console.error('Şehir eklenirken hata oluştu:', error);
    throw error;
  }
};

// Şehir güncelle
export const updateCity = async (cityId: string, updateData: Partial<Omit<FirebaseCity, 'id' | 'createdAt' | 'updatedAt'>>): Promise<boolean> => {
  try {
    // Güncelleme verisinden undefined değerleri temizle
    const cleanedData: Record<string, any> = {};
    
    Object.entries(updateData).forEach(([key, value]) => {
      // location objesi için özel işlem yap
      if (key === 'location' && value !== undefined) {
        // Konum verisini doğrula
        const locationData = value as {latitude?: number, longitude?: number} | null;
        
        // Eğer location null değilse ve içinde geçerli koordinatlar varsa ekle
        if (locationData && 
            typeof locationData.latitude === 'number' && 
            typeof locationData.longitude === 'number') {
          cleanedData.location = {
            latitude: locationData.latitude,
            longitude: locationData.longitude
          };
        } 
        // Aksi halde location alanını güncelleme verisinden çıkar
      } else if (value !== undefined) {
        // Diğer alanlar için undefined olmayan değerleri ekle
        cleanedData[key] = value;
      }
    });
    
    // Güncelleme tarihini ekle
    cleanedData.updatedAt = new Date().toISOString();
    
    const cityRef = doc(db, 'cities', cityId);
    await updateDoc(cityRef, cleanedData);
    
    console.log(`Şehir başarıyla güncellendi, ID: ${cityId}`, cleanedData);
    return true;
  } catch (error) {
    console.error('Şehir güncellenirken hata oluştu:', error);
    throw error;
  }
};

// Şehir sil
export const deleteCity = async (cityId: string): Promise<void> => {
  try {
    const cityRef = doc(db, 'cities', cityId);
    await deleteDoc(cityRef);
  } catch (error) {
    console.error('Şehir silinirken hata oluştu:', error);
    throw error;
  }
};

// Şehir ID'ye göre getir
export const getCityById = async (cityId: string): Promise<FirebaseCity | null> => {
  try {
    const cityRef = doc(db, 'cities', cityId);
    const citySnapshot = await getDoc(cityRef);
    
    if (!citySnapshot.exists()) {
      return null;
    }
    
    return {
      id: citySnapshot.id,
      ...citySnapshot.data()
    } as FirebaseCity;
  } catch (error) {
    console.error('Şehir getirilirken hata oluştu:', error);
    throw error;
  }
};

// JSON'dan Firebase veritabanına aktarım
export const migrateLocalCitiesToFirebase = async (localCities: any[]): Promise<void> => {
  try {
    for (const city of localCities) {
      // JSON verilerini Firebase formatına dönüştür
      const firebaseCity: Omit<FirebaseCity, 'id' | 'createdAt' | 'updatedAt'> = {
        countryCode: city.country_code,
        countryName: city.country_name,
        cityCode: city.city_code,
        cityName: city.city_name,
        isActive: city.city_active_status,
        apiEndpoint: city.backend_api_url,
        features: {
          bus: city['lines-stops-features']?.bus?.bus_service_available || false,
          metro: city['lines-stops-features']?.metro?.metro_service_available || false,
          tram: city['lines-stops-features']?.tram?.tram_service_available || false,
          ferry: city['lines-stops-features']?.ferry?.ferry_service_available || false,
          train: city['lines-stops-features']?.train?.train_service_available || false,
        },
        transportFeatures: city['lines-stops-features'],
        routePlanning: city.route_planning || false,
        announcements: city.announcements || false,
        description: city.description || '',
        dataSource: city.data_source || '',
        dataSourceUrl: city.data_source_url || '',
      };
      
      // Firebase'e ekle
      await addCity(firebaseCity);
    }
    
    console.log('Tüm şehirler Firebase veritabanına başarıyla aktarıldı');
  } catch (error) {
    console.error('Veri aktarımı sırasında hata oluştu:', error);
    throw error;
  }
};
